import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { HttpMethod } from '@core/http/http-method.enum';
import { ErrorService } from '@core/http/error.service';
import { handleError, makeRequest } from '@core/http/resource.utils';
import { NUGGETS_URL } from '@shared/constants/api.constants';
import {
    CreateNuggetRequest,
    MyNuggetRatingResponse,
    NuggetDetailResponse,
    NuggetResponse,
    UpdateNuggetRequest
} from '@spe-content-store/interfaces';
import { Nugget } from '@shared/models/sequence.model';
import { toNugget, toUpdateNuggetRequest } from '@shared/utils/nugget-api.utils';

@Injectable({ providedIn: 'root' })
export class NuggetService {
    private readonly httpClient = inject(HttpClient);
    private readonly errorService = inject(ErrorService);

    /** Details and the caller's own like are separate resources, so both are merged here. */
    public loadNugget(nuggetId: string): Observable<Nugget> {
        return forkJoin({
            detail: makeRequest<NuggetDetailResponse>(
                this.httpClient,
                HttpMethod.GET,
                `${NUGGETS_URL}/${nuggetId}`,
                {},
                handleError<NuggetDetailResponse>('load nugget', this.errorService, 'nugget')
            ),
            rating: this.loadMyRating(nuggetId).pipe(catchError(() => of(null)))
        }).pipe(map(({ detail, rating }) => toNugget(detail, rating?.rating?.liked ?? false)));
    }

    public setLike(nuggetId: string, liked: boolean): Observable<MyNuggetRatingResponse> {
        return makeRequest<MyNuggetRatingResponse>(
            this.httpClient,
            HttpMethod.PUT,
            `${NUGGETS_URL}/${nuggetId}/ratings/me`,
            { body: { liked } },
            handleError<MyNuggetRatingResponse>('update rating', this.errorService, 'rating')
        );
    }

    /** The backend has no partial update, so the whole nugget is sent with the changed field. */
    public setApprenticeVisibility(nugget: Nugget, apprentVis: boolean): Observable<unknown> {
        return this.updateNugget(nugget.id, toUpdateNuggetRequest(nugget, { apprentVis }));
    }

    public createNugget(request: CreateNuggetRequest): Observable<NuggetResponse> {
        return makeRequest<NuggetResponse>(
            this.httpClient,
            HttpMethod.POST,
            NUGGETS_URL,
            { body: request },
            handleError<NuggetResponse>('create nugget', this.errorService, 'nugget')
        );
    }

    public updateNugget(nuggetId: string, request: UpdateNuggetRequest): Observable<unknown> {
        return makeRequest<unknown>(
            this.httpClient,
            HttpMethod.PUT,
            `${NUGGETS_URL}/${nuggetId}`,
            { body: request },
            handleError<unknown>('update nugget', this.errorService, 'nugget')
        );
    }

    public downloadDocument(url: string): Observable<Blob> {
        return makeRequest<Blob>(
            this.httpClient,
            HttpMethod.GET,
            url,
            { responseType: 'blob' },
            handleError<Blob>('download document', this.errorService, 'nugget')
        );
    }

    private loadMyRating(nuggetId: string): Observable<MyNuggetRatingResponse> {
        return makeRequest<MyNuggetRatingResponse>(
            this.httpClient,
            HttpMethod.GET,
            `${NUGGETS_URL}/${nuggetId}/ratings/me`,
            {},
            handleError<MyNuggetRatingResponse>('load rating', this.errorService, 'rating')
        );
    }
}
